import * as React from "react";
import { Switch as AriaSwitch, type SwitchProps as AriaSwitchProps } from "react-aria-components";

import { cn } from "./utils";

export interface SwitchProps extends Omit<AriaSwitchProps, "className"> {
  className?: string;
  /** Radix-compat alias for defaultSelected */
  defaultChecked?: boolean;
  /** Radix-compat alias for isSelected */
  checked?: boolean;
  /** Radix-compat alias for onChange */
  onCheckedChange?: (checked: boolean) => void;
}

function Switch({ className, children, defaultChecked, checked, onCheckedChange, ...props }: SwitchProps) {
  return (
    <AriaSwitch
      data-slot="switch"
      className={cn(
        "group peer inline-flex items-center gap-2",
        className,
      )}
      defaultSelected={defaultChecked ?? props.defaultSelected}
      isSelected={checked ?? props.isSelected}
      onChange={onCheckedChange ?? props.onChange}
      {...props}
    >
      {({ isSelected }) => (
        <>
          <div
            data-slot="switch-track"
            className={cn(
              "inline-flex h-[1.15rem] w-8 shrink-0 items-center rounded-full border border-transparent transition-all",
              "group-focus-visible:border-ring group-focus-visible:ring-ring/50 group-focus-visible:ring-[3px]",
              "group-disabled:cursor-not-allowed group-disabled:opacity-50",
              isSelected ? "bg-primary" : "bg-switch-background dark:bg-input/80",
            )}
          >
            <div
              data-slot="switch-thumb"
              className={cn(
                "pointer-events-none block size-4 rounded-full ring-0 transition-transform",
                isSelected
                  ? "translate-x-[calc(100%-2px)] bg-primary-foreground"
                  : "translate-x-0 bg-card dark:bg-card-foreground",
              )}
            />
          </div>
          {children}
        </>
      )}
    </AriaSwitch>
  );
}

export { Switch };
